import type { FunctionComponent, SelectHTMLAttributes } from "preact";
import { useEffect } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { $cameraList, $cameraListLoaded } from "../../store/cameras.ts";

export type FarpovCameraSelectProps = {
    selectCameraId: number;
    setSelectCameraId: (newId: number) => unknown;
} & SelectHTMLAttributes;

export const FarpovCameraSelect: FunctionComponent<FarpovCameraSelectProps> = (props) => {
    const cameraList = useStore($cameraList);
    const cameraListLoaded = useStore($cameraListLoaded);

    useEffect(() => {
        if (!cameraListLoaded) return;
        let exist = false;
        for (const info of cameraList) {
            if (info.camera_id === props.selectCameraId) {
                exist = true;
                break;
            }
        }
        if (!exist) {
            if (props.selectCameraId >= 0 && cameraList.length <= 0) {
                props.setSelectCameraId(-1);
            } else if (cameraList.length > 0) {
                props.setSelectCameraId(cameraList[0].camera_id);
            }
        }
    }, [cameraList, cameraListLoaded]);

    return (
        <select
            {...props}
            style={{ ...((props.style as any) ?? {}), width: "auto", maxWidth: "200px", marginBottom: 0 }}
            onChange={(evt) => {
                const elem = evt.target as HTMLSelectElement;
                const newId = Number.parseInt(elem.value);
                if (newId != props.selectCameraId) {
                    props.setSelectCameraId(newId);
                }
            }}
        >
            {(() => {
                // Grouping logic
                const groups: Record<string, typeof cameraList> = {};
                const noGroup: typeof cameraList = [];

                cameraList.forEach(cam => {
                    const gName = (cam.group_name || "").trim();
                    if (gName) {
                        if (!groups[gName]) groups[gName] = [];
                        groups[gName].push(cam);
                    } else {
                        noGroup.push(cam);
                    }
                });

                const sortedGroupNames = Object.keys(groups).sort();
                const elements = [];

                // Render groups
                sortedGroupNames.forEach(gName => {
                    elements.push(
                        <optgroup label={gName} key={`group-${gName}`}>
                            {groups[gName].map(cam => (
                                <option 
                                    value={cam.camera_id} 
                                    key={cam.camera_id} 
                                    selected={cam.camera_id === props.selectCameraId}
                                >
                                    {cam.camera_name}
                                </option>
                            ))}
                        </optgroup>
                    );
                });

                // Render ungrouped
                if (noGroup.length > 0) {
                    // Only add label if there are other groups
                    if (sortedGroupNames.length > 0) {
                         elements.push(
                            <optgroup label="Other" key="group-other">
                                {noGroup.map(cam => (
                                    <option 
                                        value={cam.camera_id} 
                                        key={cam.camera_id} 
                                        selected={cam.camera_id === props.selectCameraId}
                                    >
                                        {cam.camera_name}
                                    </option>
                                ))}
                            </optgroup>
                        );
                    } else {
                        // Just plain options if no groups exist
                        elements.push(
                            ...noGroup.map(cam => (
                                <option 
                                    value={cam.camera_id} 
                                    key={cam.camera_id} 
                                    selected={cam.camera_id === props.selectCameraId}
                                >
                                    {cam.camera_name}
                                </option>
                            ))
                        );
                    }
                }

                return elements;
            })()}
        </select>
    );
};
export default FarpovCameraSelect;
